/**
 * Profit Chart Component
 * Shows revenue, expenses, and profit over time
 */

import React from 'react';
import {
  ChartContainer,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  CustomTooltip,
  formatCurrency,
  CHART_COLORS
} from './FinancialChart';
import { FinancialProjection, ScenarioType } from '../../../types/financialForecasting';

interface ProfitChartProps {
  data: FinancialProjection[];
  activeScenario: ScenarioType;
}

export const ProfitChart: React.FC<ProfitChartProps> = ({
  data,
  activeScenario
}) => {
  // Prepare chart data
  const chartData = data.map((projection, index) => ({
    period: `P${index + 1}`,
    revenue: projection.totalRevenue,
    expenses: projection.totalExpenses,
    profit: projection.netProfit
  }));

  return (
    <ChartContainer
      title="Revenue, Expenses & Profit"
      subtitle={`${activeScenario.charAt(0).toUpperCase() + activeScenario.slice(1)} scenario breakdown`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="period"
            stroke="#6B7280"
            fontSize={12}
            tickLine={false}
          />
          <YAxis
            stroke="#6B7280"
            fontSize={12}
            tickFormatter={formatCurrency}
            tickLine={false}
          />
          <Tooltip
            content={<CustomTooltip formatter={formatCurrency} />}
          />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="circle"
          />
          <Bar
            dataKey="revenue"
            name="Revenue"
            fill={CHART_COLORS.primary}
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="expenses"
            name="Expenses"
            fill={CHART_COLORS.warning}
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="profit"
            name="Profit"
            fill={CHART_COLORS.success}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

export default ProfitChart;
